import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { toast } from "react-toastify";
import API_BASE_URL from "../config/api";

export default function EditAttendanceDialog({ open, onClose, staffData, selectedDate, onSave }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("Present");
  const [punchInTime, setPunchInTime] = useState("");
  const [punchOutTime, setPunchOutTime] = useState("");
  const [breaks, setBreaks] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (open && staffData) {
      // Check if this is creating new attendance (no AttendanceId)
      const creating = !staffData.AttendanceId;
      setIsCreating(creating);
      
      // Initialize form with current values
      // Convert "Late" status to "Present" since we removed the Late option
      const initialStatus = staffData.Status === "Late" ? "Present" : (staffData.Status || "Present");
      setStatus(initialStatus);
      setPunchInTime(formatTimeForInput(staffData.PunchInTime) || "09:00");
      setPunchOutTime(staffData.PunchOutTime ? formatTimeForInput(staffData.PunchOutTime) : "");
      
      // console.log('Staff data breaks:', staffData.Breaks);
      
      setBreaks(
        staffData.Breaks?.map((b) => {
          console.log('Break item:', b);
          return {
            breakId: b.BreakId || b.breakId,
            breakStartTime: formatTimeForInput(b.BreakStartTime),
            breakEndTime: b.BreakEndTime ? formatTimeForInput(b.BreakEndTime) : "",
          };
        }) || []
      );
      setErrors({});
    }
  }, [open, staffData]);

  const formatTimeForInput = (timeString) => {
    if (!timeString) return "";
    // Convert HH:MM:SS to HH:MM
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
      return timeString.substring(0, 5);
    }
    return timeString;
  };

  const validateTimes = () => {
    const newErrors = {};

    // If status is Absent, no time validation needed
    if (status === "Absent") {
      setErrors({});
      return true;
    }

    // Validate punch in time for Present/Late status
    if (!punchInTime) {
      newErrors.punchInTime = "Punch in time is required";
    }

    // Validate punch in is before punch out
    if (punchInTime && punchOutTime) {
      const punchIn = new Date(`2000-01-01T${punchInTime}`);
      const punchOut = new Date(`2000-01-01T${punchOutTime}`);
      if (punchIn >= punchOut) {
        newErrors.punchOutTime = "Punch out time must be after punch in time";
      }
    }

    // Validate breaks
    breaks.forEach((breakItem, index) => {
      if (!breakItem.breakStartTime) {
        newErrors[`break${index}Start`] = "Break start time is required";
      }

      // Validate break start is after punch in
      if (punchInTime && breakItem.breakStartTime) {
        const punchIn = new Date(`2000-01-01T${punchInTime}`);
        const breakStart = new Date(`2000-01-01T${breakItem.breakStartTime}`);
        if (breakStart < punchIn) {
          newErrors[`break${index}Start`] = "Break start must be after punch in";
        }
      }

      // Validate break end is after break start
      if (breakItem.breakStartTime && breakItem.breakEndTime) {
        const breakStart = new Date(`2000-01-01T${breakItem.breakStartTime}`);
        const breakEnd = new Date(`2000-01-01T${breakItem.breakEndTime}`);
        if (breakEnd <= breakStart) {
          newErrors[`break${index}End`] = "Break end must be after break start";
        }
      }

      // Validate break is within punch in/out window (only if punch out is set)
      if (punchOutTime && breakItem.breakEndTime) {
        const punchOut = new Date(`2000-01-01T${punchOutTime}`);
        const breakEnd = new Date(`2000-01-01T${breakItem.breakEndTime}`);
        if (breakEnd > punchOut) {
          newErrors[`break${index}End`] = "Break end must be before punch out";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateTimes()) {
      toast.error("Please fix the validation errors");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // If creating new attendance, use admin-create endpoint
      if (isCreating) {
        const payload = {
          staffId: staffData.StaffId,
          date: selectedDate,
          status,
          punchInTime: status === "Absent" ? null : punchInTime,
          punchOutTime: status === "Absent" ? null : (punchOutTime || null),
          breaks: status === "Absent" ? [] : breaks
            .filter((b) => b.breakStartTime) // Only include breaks with start time
            .map((b) => ({
              breakStartTime: b.breakStartTime,
              breakEndTime: b.breakEndTime || null,
            })),
        };

        console.log('Creating attendance with payload:', payload);

        const response = await fetch(
          `${API_BASE_URL}/api/time-tracking/admin-create-attendance`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );

        const data = await response.json();

        if (response.ok) {
          toast.success("Attendance created successfully");
          onSave();
          onClose();
        } else {
          toast.error(data.message || "Failed to create attendance");
        }
      } else {
        // Updating existing attendance
        const payload = {
          status,
          punchInTime: status === "Absent" ? null : punchInTime,
          punchOutTime: status === "Absent" ? null : (punchOutTime || null),
          breaks: status === "Absent" ? [] : breaks
            .filter((b) => b.breakId || b.breakStartTime) // Include existing breaks or new breaks with start time
            .map((b) => ({
              breakId: b.breakId,
              breakStartTime: b.breakStartTime,
              breakEndTime: b.breakEndTime || null,
            })),
        };

        console.log('Sending payload:', payload);
        console.log('Attendance ID:', staffData.AttendanceId);

        const response = await fetch(
          `${API_BASE_URL}/api/time-tracking/attendance/${staffData.AttendanceId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );

        const data = await response.json();

        if (response.ok) {
          toast.success("Attendance updated successfully");
          onSave();
          onClose();
        } else {
          toast.error(data.message || "Failed to update attendance");
        }
      }
    } catch (error) {
      console.error("Save attendance error:", error);
      toast.error("Failed to save attendance");
    } finally {
      setLoading(false);
    }
  };

  const handleBreakChange = (index, field, value) => {
    const newBreaks = [...breaks];
    newBreaks[index][field] = value;
    setBreaks(newBreaks);
  };

  const handleAddBreak = () => {
    setBreaks([
      ...breaks,
      {
        breakId: null, // New break, no ID yet
        breakStartTime: "",
        breakEndTime: "",
      },
    ]);
  };

  const handleRemoveBreak = (index) => {
    const newBreaks = breaks.filter((_, i) => i !== index);
    setBreaks(newBreaks);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: "#2b2b2b", color: "white", fontWeight: 600 }}>
        Edit Attendance - {staffData?.FullName}
      </DialogTitle>
      <DialogContent sx={{ mt: 2, bgcolor: "#4a4a4a" }}>
        <Typography variant="body2" sx={{ mb: 2, color: "#9CA3AF" }}>
          Date: {new Date(selectedDate).toLocaleDateString("en-IN", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Typography>

        {/* Status Dropdown */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel sx={{ color: "#9CA3AF" }}>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => setStatus(e.target.value)}
            sx={{
              color: "#e5e7eb",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#6B7280" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9CA3AF" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#C4A574" },
              "& .MuiSvgIcon-root": { color: "#9CA3AF" }
            }}
          >
            <MenuItem value="Present">Present</MenuItem>
            <MenuItem value="Absent">Absent</MenuItem>
          </Select>
        </FormControl>

        {status !== "Absent" && (
          <>
            <Grid container spacing={2}>
              {/* Punch In Time */}
              <Grid item xs={6}>
            <TextField
              label="Punch In Time"
              type="time"
              value={punchInTime}
              onChange={(e) => setPunchInTime(e.target.value)}
              fullWidth
              error={!!errors.punchInTime}
              helperText={errors.punchInTime}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
              sx={{
                "& .MuiInputLabel-root": { color: "#9CA3AF" },
                "& .MuiOutlinedInput-root": {
                  color: "#e5e7eb",
                  "& fieldset": { borderColor: "#6B7280" },
                  "&:hover fieldset": { borderColor: "#9CA3AF" },
                  "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                },
                "& .MuiFormHelperText-root": { color: "#F44336" }
              }}
            />
          </Grid>

              {/* Punch Out Time */}
              <Grid item xs={6}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    label="Punch Out Time (Leave empty for ongoing)"
                    type="time"
                    value={punchOutTime}
                    onChange={(e) => setPunchOutTime(e.target.value)}
                    fullWidth
                    error={!!errors.punchOutTime}
                    helperText={errors.punchOutTime || "Leave empty if shift is still ongoing"}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    sx={{
                      "& .MuiInputLabel-root": { color: "#9CA3AF" },
                      "& .MuiOutlinedInput-root": {
                        color: "#e5e7eb",
                        "& fieldset": { borderColor: "#6B7280" },
                        "&:hover fieldset": { borderColor: "#9CA3AF" },
                        "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                      },
                      "& .MuiFormHelperText-root": { color: errors.punchOutTime ? "#F44336" : "#9CA3AF" }
                    }}
                  />
                  {punchOutTime && (
                    <Button
                      size="small"
                      onClick={() => setPunchOutTime("")}
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        minWidth: "auto",
                        padding: "4px 8px",
                        fontSize: "0.75rem",
                        bgcolor: "#6B7280",
                        color: "#e5e7eb",
                        "&:hover": { bgcolor: "#9CA3AF" },
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>

            {!punchOutTime && (
              <Box sx={{ p: 2, bgcolor: "#2d4a4a", borderRadius: 1, mt: 2 }}>
                <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                  <strong>Ongoing Shift:</strong> Punch out time is not set. The shift is still in progress.
                </Typography>
              </Box>
            )}

            {/* Breaks Section */}
            <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.1)" }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#e5e7eb" }}>
                Breaks
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleAddBreak}
                sx={{
                  borderColor: "#C4A574",
                  color: "#C4A574",
                  bgcolor: "transparent",
                  "&:hover": { borderColor: "#FFB74D", bgcolor: "rgba(196, 165, 116, 0.1)" },
                }}
              >
                + Add Break
              </Button>
            </Box>

            {breaks.length === 0 && (
              <Box sx={{ p: 2, bgcolor: "#3d3d3d", borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                  No breaks recorded. Click "Add Break" to add one.
                </Typography>
              </Box>
            )}

            {breaks.map((breakItem, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, bgcolor: "#3d3d3d", borderRadius: 1, border: "1px solid #6B7280" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#e5e7eb" }}>
                    Break {index + 1}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => handleRemoveBreak(index)}
                    sx={{
                      color: "#F44336",
                      minWidth: "auto",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      "&:hover": { bgcolor: "rgba(244, 67, 54, 0.1)" },
                    }}
                  >
                    Remove
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Break Start"
                      type="time"
                      value={breakItem.breakStartTime}
                      onChange={(e) => handleBreakChange(index, "breakStartTime", e.target.value)}
                      fullWidth
                      error={!!errors[`break${index}Start`]}
                      helperText={errors[`break${index}Start`]}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      sx={{
                        "& .MuiInputLabel-root": { color: "#9CA3AF" },
                        "& .MuiOutlinedInput-root": {
                          color: "#e5e7eb",
                          "& fieldset": { borderColor: "#6B7280" },
                          "&:hover fieldset": { borderColor: "#9CA3AF" },
                          "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                        },
                        "& .MuiFormHelperText-root": { color: "#F44336" }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ position: "relative" }}>
                      <TextField
                        label="Break End"
                        type="time"
                        value={breakItem.breakEndTime}
                        onChange={(e) => handleBreakChange(index, "breakEndTime", e.target.value)}
                        fullWidth
                        error={!!errors[`break${index}End`]}
                        helperText={errors[`break${index}End`]}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        sx={{
                          "& .MuiInputLabel-root": { color: "#9CA3AF" },
                          "& .MuiOutlinedInput-root": {
                            color: "#e5e7eb",
                            "& fieldset": { borderColor: "#6B7280" },
                            "&:hover fieldset": { borderColor: "#9CA3AF" },
                            "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                          },
                          "& .MuiFormHelperText-root": { color: "#F44336" }
                        }}
                      />
                      {breakItem.breakEndTime && (
                        <Button
                          size="small"
                          onClick={() => handleBreakChange(index, "breakEndTime", "")}
                          sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            minWidth: "auto",
                            padding: "4px 8px",
                            fontSize: "0.75rem",
                            bgcolor: "#6B7280",
                            color: "#e5e7eb",
                            "&:hover": { bgcolor: "#9CA3AF" },
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </>
        )}

        {status === "Absent" && (
          <Box sx={{ p: 2, bgcolor: "#3d3d3d", borderRadius: 1, mt: 2, border: "1px solid #6B7280" }}>
            <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
              <strong>Note:</strong> Marking as Absent will clear all time tracking data (punch in/out times and breaks).
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: "#4a4a4a" }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{
            color: "#9CA3AF",
            "&:hover": { bgcolor: "rgba(156, 163, 175, 0.1)" }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{
            bgcolor: "#C4A574",
            color: "#ffffff",
            "&:hover": { bgcolor: "#8B7355" },
            "&:disabled": { bgcolor: "#6B7280", color: "#9CA3AF" }
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: "#9CA3AF" }} /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
